import { useMutation, useQuery } from "@tanstack/react-query";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { add, eachMonthOfInterval, endOfMonth, format, parse, startOfMonth, sub } from "date-fns";
import { ApiVenueStaffInfo, ClimbType } from "griptonite-api-client";
import { HTTPError } from "griptonite-api-client/client";
import {
  ApiTask,
  ApiTaskUpdatePayload,
  ApiTaskSet,
  TaskStatus,
  ApiTaskSetCreatePayload,
  ApiTaskSetUpdatePayload,
} from "griptonite-api-client/types/tasks";
import { useMemo } from "react";

import { useModal } from "@/components/modals/useModal";
import { useTaskSetOverviewStore } from "@/components/TaskSetOverview/useTaskSetOverview";
import { convertToInternal } from "@/utils/grades";

import api from "../core/api";
import queryClient from "../core/queryClient";
import { Task, TaskSet } from "../core/types";

export const useVenueTaskSets = <TData = ApiTaskSet[]>(
  venueUuid?: string,
  month?: string,
  select?: (data: ApiTaskSet[]) => TData,
) => {
  // month is in form YYYY-MM
  console.log({ month });
  return useQuery({
    queryKey: ["venues", venueUuid, "tasksets", month],
    queryFn: async () => {
      if (!month || !venueUuid) {
        // This should never happen
        throw new Error();
      }
      // Get the previous and next months too
      const thisMonthStart = parse(month, "yyyy-MM", new Date());
      const prevMonthStart = sub(thisMonthStart, { months: 1 });
      const nextMonthEnd = endOfMonth(add(thisMonthStart, { months: 1 }));
      const result = await api.tasks.getTasksets(venueUuid, {
        since: format(prevMonthStart, "yyyy-MM-dd"),
        until: format(nextMonthEnd, "yyyy-MM-dd"),
      });
      return result.data;
    },
    enabled: !!venueUuid && !!month,
    select,
    placeholderData: () => {
      if (!month || !venueUuid) {
        return undefined;
      }
      const thisMonthStart = parse(month, "yyyy-MM", new Date());
      const thisMonthEnd = endOfMonth(thisMonthStart);
      const thisMonthEndStr = format(thisMonthEnd, "yyyy-MM-dd");
      const prevMonth = format(sub(thisMonthStart, { months: 1 }), "yyyy-MM");
      const nextMonth = format(add(thisMonthStart, { months: 1 }), "yyyy-MM");
      const prevMonthData: TaskSet[] | undefined = queryClient.getQueryData([
        "venues",
        venueUuid,
        "tasksets",
        prevMonth,
      ]);
      const nextMonthData: TaskSet[] | undefined = queryClient.getQueryData([
        "venues",
        venueUuid,
        "tasksets",
        nextMonth,
      ]);
      if (prevMonthData) {
        return prevMonthData.filter(
          (set) => set.endDate >= month || set.startDate <= thisMonthEndStr,
        );
      }
      if (nextMonthData) {
        return nextMonthData.filter(
          (set) => set.endDate >= month || set.startDate <= thisMonthEndStr,
        );
      }
      return undefined;
    },
  });
};

const findTasksSetInCache = (venueUuid: string, uuid: string) => {
  const matchingQueries = queryClient.getQueriesData<ApiTaskSet[]>({
    queryKey: ["venues", venueUuid, "tasksets"],
  });
  for (const [_, data] of matchingQueries) {
    if (data) {
      const taskSet = data.find((set: TaskSet) => set.uuid === uuid);
      if (taskSet) {
        return taskSet;
      }
    }
  }
  return undefined;
};

export const useTaskSetQuery = (uuid?: string, venueUuid?: string) => {
  return useQuery({
    queryKey: ["tasksets", uuid],
    queryFn: () => api.tasks.getTaskset(uuid!) as Promise<TaskSet>,
    enabled: !!uuid,
    placeholderData: () => {
      return findTasksSetInCache(venueUuid!, uuid!);
    },
  });
};

export const useTaskSetForCompetitionQuery = (competitionUuid?: string) => {
  return useQuery({
    queryKey: ["tasksets", "for-competition", competitionUuid],
    queryFn: async () => {
      try {
        const taskSet = api.tasks.getTaskSetForCompetition(competitionUuid!);
        return taskSet;
      } catch (error) {
        if (error instanceof HTTPError && error.response.status === 404) {
          return null;
        }
      }
    },
    enabled: !!competitionUuid,
    staleTime: 5000,
  });
};

export const useTaskQuery = (uuid?: string, tasksetUuid?: string) => {
  return useQuery({
    queryKey: ["tasks", uuid],
    queryFn: () => api.tasks.getTask(uuid!) as Promise<Task>,
    enabled: !!uuid,
    placeholderData: () => {
      const taskSetData: TaskSet | undefined = queryClient.getQueryData(["tasksets", tasksetUuid]);
      return taskSetData?.tasks?.find((task) => task.uuid === uuid);
    },
  });
};

const replaceTaskInArray = (
  tasks: ApiTask[] | undefined,
  uuid: string,
  newTask: ApiTask,
): ApiTask[] => {
  if (!tasks) {
    return [newTask];
  }
  const index = tasks.findIndex((r) => r.uuid === uuid);
  if (index === -1) {
    return [...tasks, newTask];
  }
  return [...tasks.slice(0, index), newTask, ...tasks.slice(index + 1)];
};

type TaskCreatePayload = {
  set?: string;
  venue: string;
  type: "SET" | "STRIP" | "MISC";
  allocatedDuration?: number;
  status?: TaskStatus;
  climbType?: ClimbType;
  dueDate?: string;
  gradeSystem?: string | null;
  gradeValue?: string | null;
  assignees?: string[];
  route?: {
    circuit: { uuid: string | null } | null;
    zone: string | null;
    gradeValue: string | null;
    gradeSystem: string | null;
    gradeInternal?: number | null;
    startTime?: string;
    endTime?: string;
    map?: string | null;
    climbType: ClimbType;
    venue: string;
  } | null;
};

export const useCreateTaskMutation = () => {
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: ["createTask"],
    mutationFn: (task: TaskCreatePayload & { uuid: string }) => {
      // // remove temporary uuid
      // const { uuid: _uuid, ...data } = task;
      // if (data.route && typeof data.route === "object") {
      //   data.route = { ...data.route };
      //   delete data.route.gradeInternal;
      // }
      const payload = {
        dueDate: task.dueDate,
        climbType: task.climbType,
        type: task.type,
        venue: task.venue,
        set: task.set,
        allocatedDuration: task.allocatedDuration || null,
        gradeValue: task.gradeValue,
        gradeSystem: task.gradeSystem,
        status: task.status || "TODO",
        assignees: task.assignees,
        route: task.route
          ? {
              circuit: task.route.circuit ? { uuid: task.route.circuit.uuid } : null,
              zone: task.route.zone || null,
              gradeValue: task.route.gradeValue,
              gradeSystem: task.route.gradeSystem,
              startTime: task.route.startTime || null,
              endTime: task.route.endTime || null,
              map: task.route.map || null,
              mapPosition: null,
              climbType: task.route.climbType,
              venue: task.venue,
            }
          : null,
      };
      return api.tasks.createTask(payload);
    },
    onMutate: (taskPayload: TaskCreatePayload & { uuid: string }) => {
      const allStaffUsers = queryClient.getQueryData<ApiVenueStaffInfo[]>([
        "venues",
        taskPayload.venue,
        "staff",
      ]);
      const assignees = (taskPayload.assignees || []).map(
        (uuid) => allStaffUsers?.find((info) => info.user.uuid === uuid)?.user,
      );

      const task = {
        ...taskPayload,
        assignees, //todo - add the assignee here from the query client, if provided
        creationTime: new Date().toISOString(),
        dueDate: new Date().toISOString(),
        name: null,
        notes: "",
        creator: null,
        startTime: null,
        completionTime: null,
        route: taskPayload.route
          ? {
              ...taskPayload.route,
              uuid: crypto.randomUUID(),
              holds: [],
              skills: [],
              styles: [],
              name: null,
              description: null,
              colors: null,
              quickdrawCount: null,
              startLine: null,
              creationTime: null,
              setters: [],
              height: null,
              scheduledEndTime: null,
              endTime: null,
              pinGradeValue: null,
              segmentsCount: null,
              gradeInternal: taskPayload.route.gradeInternal,
            }
          : null,
      } as ApiTask;
      queryClient.cancelQueries({ queryKey: ["tasksets", task.set] });
      const previousTaskSet = queryClient.getQueryData<TaskSet>(["tasksets", task.set]);
      if (queryClient.getQueryData(["tasksets", task.set])) {
        queryClient.setQueryData(["tasksets", task.set], (existingData: TaskSet) => ({
          ...existingData,
          tasks: [...(existingData.tasks || []), task],
        }));
      }
      queryClient.setQueryData(["tasks", task.uuid], task);
      return { previousTaskSet, task };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(["tasksets", context.task.set], context.previousTaskSet);
      }
    },
    onSuccess: (data, _variables, context) => {
      if (queryClient.getQueryData(["tasksets", data.set])) {
        queryClient.setQueryData(["tasksets", data.set], (existingData: TaskSet) => ({
          ...existingData,
          tasks: replaceTaskInArray(existingData.tasks, context.task.uuid, data),
        }));
      }
      queryClient.setQueryData(["tasks", data.uuid], data as ApiTask);
      // If the current location is the temporary task's page, redirect to the correct uuid
      if (
        matchRoute({
          to: "/gym/$venueUuid/schedule/set/$setUuid/$taskUuid",
          params: { taskUuid: context.task.uuid },
        })
      ) {
        navigate({
          to: `/gym/$venueUuid/schedule/set/$setUuid/$taskUuid`,
          params: {
            venueUuid: data.venue,
            setUuid: data.set,
            taskUuid: data.uuid,
          },
          replace: true,
        });
      }
      // remove task with temporary uuid
      queryClient.removeQueries({
        queryKey: ["tasks", context.task.uuid],
      });
    },
  });
};

export const useUpdateTaskMutation = (venueUuid: string | undefined) => {
  return useMutation({
    mutationFn: (variables: ApiTaskUpdatePayload & { uuid: string }) => {
      const { uuid, ...task } = variables;
      if (!venueUuid) {
        throw new Error("venueUuid is required");
      }
      return api.tasks.updateTask(uuid, task);
    },
    onSuccess: (data) => {
      if (!venueUuid) {
        throw new Error("venueUuid is required");
      }
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "routes"],
      });
      queryClient.setQueryData(["tasksets", data.set], (existingData: TaskSet) => ({
        ...existingData,
        tasks: replaceTaskInArray(existingData.tasks, data.uuid, data),
      }));
      queryClient.setQueryData(["tasks", data.uuid], data as ApiTask);
    },
  });
};

class TaskValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TaskValidationError";
  }
}

type TaskUpdatePayload = {
  status?: TaskStatus;
  route?: {
    gradeValue?: string | null;
    gradeSystem?: string;
    gradeInternal?: number | null;
    startTime?: string | null;
    endTime?: string | null;
  };
};

// TODO: move me elsewhere!
const validateTaskUpdate = (task: ApiTask, update: TaskUpdatePayload) => {
  if (update.status === "DONE" && task.status !== "DONE") {
    // Check that all necessary fields are present
    if (task.type === "SET") {
      if (!task.route) {
        throw new TaskValidationError("Route is required");
      }
      if (!task.route.climbType) {
        throw new TaskValidationError("Climb type is required");
      }
      if (task.route.climbType === "BOULDER") {
        if (!task.route.circuit?.uuid && !task.route.competitionRouteGroups) {
          throw new TaskValidationError("Circuit is required");
        }
      } else if (task.route.climbType === "SPORT") {
        if (!task.route.startLine && !task.route.competitionRouteGroups) {
          throw new TaskValidationError("Line is required");
        }
      }
      if (!task.route.gradeValue && !task.route.competitionRouteGroups) {
        throw new TaskValidationError(
          "No grade set. Give the route a grade so that climbers can get points awarded. If grade is hidden it will not be visible to climbers.",
        );
      }
      if (!task.route.map || !task.route.mapPosition) {
        throw new TaskValidationError(
          "Route has no position.  Position the route by dragging the pin on the map",
        );
      }
    } else if (task.type === "STRIP") {
      if (!task.route) {
        throw new TaskValidationError("Route is required");
      }
    }
  }

  if (
    update.status &&
    update.status !== "DONE" &&
    task.status === "DONE" &&
    task.type === "SET" &&
    task.route?.endTime
  ) {
    // Unpublishing a stripped route would leave it in the archive having never
    // been published. Mirrors the check on the API.
    throw new TaskValidationError(
      "This route has been stripped. Restore it first by moving its stripping task out of Done.",
    );
  }
};

export const useOptimisticTaskUpdateMutation = (
  venueUuid: string | undefined,
  validate: boolean = true,
  displayModalOnValidationFailed: boolean = true,
) => {
  const { setLastAction, setLastActionState } = useTaskSetOverviewStore();
  const { display: displayModal } = useModal();
  return useMutation({
    mutationFn: ({ task, payload }: { task: ApiTask; payload: TaskUpdatePayload }) => {
      if (!venueUuid) {
        throw new Error("venueUuid is required");
      }
      if (validate) {
        try {
          validateTaskUpdate(task, payload);
        } catch (e) {
          if (e instanceof TaskValidationError) {
            if (displayModalOnValidationFailed) {
              displayModal("TASK_VALIDATION_FAILED", {
                message: e.message,
              });
            }
            return Promise.reject(e);
          }
        }
      }
      return api.tasks.updateTask(task.uuid, payload);
    },
    onMutate: ({ task, payload }) => {
      if (!venueUuid) {
        throw new Error("venueUuid is required");
      }
      const updatedTask = {
        ...task,
        ...payload,
        route: task.route
          ? {
              ...task.route,
              ...payload.route,
            }
          : null,
      };
      console.log("optimistic mutation", {
        task,
        updatedTask,
        payload,
      });
      queryClient.setQueryData(["tasks", task.uuid], () => updatedTask);
      queryClient.setQueryData(["tasksets", task.set], (existingData: TaskSet) => ({
        ...existingData,
        tasks: replaceTaskInArray(existingData.tasks, task.uuid, updatedTask),
      }));
      setLastAction({
        changes: [
          {
            task: updatedTask,
            prevStatus: task.status,
          },
        ],
        mutationState: "loading",
      });
      return { task, previousTask: task };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["tasksets", data.set], (existingData: TaskSet) => ({
        ...existingData,
        tasks: replaceTaskInArray(existingData.tasks, data.uuid, data),
      }));
      queryClient.setQueryData(["tasks", data.uuid], () => data as ApiTask);
      setLastActionState("success", data.uuid);
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "routes"],
      });
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(["tasks", context.task.uuid], context.previousTask);
        queryClient.setQueryData(["tasksets", context.task.set], (existingData: TaskSet) => ({
          ...existingData,
          tasks: replaceTaskInArray(existingData.tasks, context.task.uuid, context.previousTask),
        }));
        setLastActionState("error", context.task.uuid);
      }
    },
  });
};

export const useCreateTaskSetMutation = () => {
  return useMutation({
    mutationFn: (data: ApiTaskSetCreatePayload) => api.tasks.createTaskSet(data),
    onSuccess: (data) => {
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);
      const startMonth = sub(startOfMonth(startDate), { months: 1 });
      const endMonth = add(startOfMonth(endDate), { months: 1 });
      const months = eachMonthOfInterval({
        start: startMonth,
        end: endMonth,
      }).map((x) => format(x, "yyyy-MM"));
      // get all the months in the range [startMonth, endMonth]
      months.forEach((month) => {
        queryClient.setQueryData(
          ["venues", data.venue, "tasksets", month],
          (existingData: ApiTaskSet[]) => (existingData ? [...existingData, data] : undefined),
        );
      });
    },
  });
};

export const useUpdateTaskSetMutation = () => {
  return useMutation({
    mutationFn: ({ uuid, payload }: { uuid: string; payload: ApiTaskSetUpdatePayload }) =>
      api.tasks.updateTaskSet(uuid, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(["tasksets", data.uuid], data);
      queryClient.invalidateQueries({
        queryKey: ["venues", data.venue, "tasksets"],
      });
    },
  });
};

export const useCreateTask = () => {
  const mutation = useCreateTaskMutation();
  const navigate = useNavigate();
  return useMemo(
    () => ({
      createTask: async (taskPayload: TaskCreatePayload & { set: string }) => {
        const uuid = crypto.randomUUID();
        if (
          taskPayload.route?.gradeValue &&
          taskPayload.route.gradeSystem &&
          !taskPayload.route.gradeInternal
        ) {
          taskPayload.route.gradeInternal = convertToInternal(
            taskPayload.route.gradeValue,
            taskPayload.route.gradeSystem,
          );
        }
        mutation.mutate({ ...taskPayload, uuid });
        navigate({
          to: `/gym/$venueUuid/schedule/set/$setUuid/$taskUuid`,
          params: {
            venueUuid: taskPayload.venue,
            setUuid: taskPayload.set,
            taskUuid: uuid,
          },
          replace: true,
        });
      },
    }),
    [mutation, navigate],
  );
};

export const useDeleteTaskMutation = (venueUuid?: string) => {
  return useMutation({
    mutationFn: (uuid: string) => api.tasks.deleteTask(uuid),
    onSuccess: (_, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["tasks", uuid] });
      queryClient.setQueriesData(
        {
          queryKey: ["tasksets"],
        },
        (taskset?: TaskSet) => {
          if (!taskset) {
            return;
          }
          return {
            ...taskset,
            tasks: taskset.tasks?.filter((task) => task.uuid !== uuid),
          };
        },
      );
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "routes"],
        });
      }
      // Deleting a setting task can take its route with it, and the route
      // cascades to any stripping task. Refetch rather than trusting the local
      // filter above, which only knows about the task we asked to delete.
      queryClient.invalidateQueries({ queryKey: ["tasksets"] });
    },
  });
};

export const useDeleteTaskSetMutation = (venueUuid?: string) => {
  return useMutation({
    mutationFn: (uuid: string) => api.tasks.deleteTaskSet(uuid),
    onSuccess: (_, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["tasksets", uuid] });
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "tasksets"],
        });
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "routes"],
        });
      }
    },
  });
};
